import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import { Input, Modal } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardHandlers } from '../../../lib/dashboard-page';
import { AuthAlert } from '../../auth/components/auth-alert';
import { CompanyWebsiteInput } from '../../auth/components/company-website-input';

const COMPONENT = 'features:no_team.components.create_team_form' satisfies I18nPrefix;

const APP_REGISTRATION_SOURCE = 'dashboard_new_team';

type CreateTeamResponse = { team: { id: string } };
type CreateTeamValidationError = { errors: Record<string, string[]> };

type CreateTeamFormProps = {
  handlers: DashboardHandlers;
  isOpen: boolean;
  onClose: () => void;
  onTeamCreated: (teamId: string) => void;
};

export function CreateTeamForm({ handlers, isOpen, onClose, onTeamCreated }: CreateTeamFormProps) {
  const { apiHandler } = handlers;
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [genericError, setGenericError] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: { companyName: '', companyWebsite: '' },
    onSubmit: async ({ value }) => {
      setErrors({});
      setGenericError(null);
      try {
        const data = await apiHandler.fetch<CreateTeamResponse>({
          method: 'POST',
          path: '/team',
          payload: {
            company_name: value.companyName,
            company_website: value.companyWebsite ? `https://${value.companyWebsite}` : '',
            app_registration_source: APP_REGISTRATION_SOURCE,
            is_partner: false,
          },
        });
        form.reset();
        onTeamCreated(data.team.id);
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 422) {
          setErrors((err.data as CreateTeamValidationError).errors);
        } else {
          setGenericError(t(`${COMPONENT}.generic_error`));
        }
      }
    },
  });

  function handleClose() {
    form.reset();
    setErrors({});
    setGenericError(null);
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={{
        text: t(`${COMPONENT}.submit`),
        onClick: form.handleSubmit,
        loading: form.state.isSubmitting,
      }}
      secondaryAction={{ text: t(`${COMPONENT}.cancel`), onClick: handleClose }}
    >
      <Modal.Header>{t(`${COMPONENT}.heading`)}</Modal.Header>
      <Modal.Body>
        {genericError && <AuthAlert type="error">{genericError}</AuthAlert>}
        <p className="text-gray-600 text-sm">
          {t(`${COMPONENT}.description`)}{' '}
          <a
            href="https://docs.sparklayer.io/account-settings#nQ3DC"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            {t(`${COMPONENT}.learn_more`)}
          </a>
        </p>

        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="companyName">
            {(field) => (
              <Input
                label={t(`${COMPONENT}.company_name_label`)}
                description={t(`${COMPONENT}.company_name_description`)}
                value={field.state.value}
                onChange={field.handleChange}
                error={errors.company_name?.[0]}
              />
            )}
          </form.Field>

          <div>
            <form.Field name="companyWebsite">
              {(field) => (
                <CompanyWebsiteInput
                  required
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
            {errors.company_website ? (
              <p className="mt-2 text-red-600 text-xs">{errors.company_website[0]}</p>
            ) : (
              <p className="mt-2 text-gray-500 text-xs">
                {t(`${COMPONENT}.company_website_description`)}
              </p>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
